import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Datatable from "../../../components/ui/datatable/Datatable";
import { BtnEnviarGenerarExcel } from "../../../components/BtnObtenerExcel";
import MonitorModal from "./MonitorModal";
import { useMonitor } from "./useMonitor";
import { BtnReenvio } from "./BtnReenvio";
import "../../../css/modal.css";

const EstadoColaboradorBadge = ({ estado }) => {
  const estadoTexto = estado || "Sin Información";
  let badgeClass = "bg-secondary";
  const lower = String(estadoTexto).toLowerCase();
  if (lower.includes("activo") || lower.includes("firmado") || lower.includes("aprobado")) {
    badgeClass = "bg-success";
  } else if (lower.includes("inactivo") || lower.includes("rechazado") || lower.includes("baja") || lower.includes("cancelado")) {
    badgeClass = "bg-danger";
  } else if (lower.includes("licencia") || lower.includes("vacaciones") || lower.includes("permiso") || lower.includes("espera") || lower.includes("pendiente")) {
    badgeClass = "bg-warning text-dark";
  }
  return (
    <span className={`badge rounded-pill ${badgeClass} px-2 py-1 fw-semibold`} style={{ fontSize: "0.78rem" }}>
      {estadoTexto}
    </span>
  );
};

EstadoColaboradorBadge.propTypes = {
  estado: PropTypes.string,
};

const renderEstadoColaborador = row => <EstadoColaboradorBadge estado={row?.estado_colaborador} />;
const renderBtnReenvioWebdox = (row, reenviarContrato) => (
  <BtnReenvio dato={row?.reenvio_contrato} id={row?.id} reenviarContrato={reenviarContrato} />
);

const getMonitorWebdoxColumns = reenviarContrato => [
  { label: "ID Contrato", sortable: true, searchForColumn: true, key: "id", selector: row => (row.id ? row.id : "---"), headerAlign: "left", dataAlign: "left", width: "110px", isVisible: false },
  { label: "N° Proceso", sortable: true, searchForColumn: true, key: "IdLote", selector: row => row.IdLote, headerAlign: "left", dataAlign: "left", width: "110px" },
  { label: "N° Personal", sortable: true, searchForColumn: true, key: "NPersonal", selector: row => (row.NPersonal ? row.NPersonal : "---"), headerAlign: "left", dataAlign: "left", width: "110px" },
  { label: "Nombre", sortable: true, searchForColumn: true, key: "Nombre", selector: row => row.Nombre, headerAlign: "left", dataAlign: "left", width: "200px" },
  { label: "RUT", sortable: true, searchForColumn: true, key: "Rut", selector: row => row.Rut, headerAlign: "left", dataAlign: "left", width: "120px" },
  { label: "División", sortable: true, searchForColumn: true, key: "Division", selector: row => row.Division, headerAlign: "left", dataAlign: "left", width: "120px" },
  { label: "Unidad organizativa", sortable: true, searchForColumn: true, key: "TextoUnidadOrganizativa", selector: row => row.TextoUnidadOrganizativa, headerAlign: "left", dataAlign: "left", width: "200px" },
  { label: "Subdivisión", sortable: true, searchForColumn: true, key: "Subdivision", selector: row => row.Subdivision, headerAlign: "left", dataAlign: "left", width: "150px" },
  { label: "Cargo", sortable: true, searchForColumn: true, key: "Cargo", selector: row => row.Cargo, headerAlign: "left", dataAlign: "left", width: "200px" },
  { label: "Fecha ingreso", sortable: true, searchForColumn: true, key: "FechaIngreso", selector: row => row?.FechaIngreso?.replaceAll(".", "-"), headerAlign: "left", dataAlign: "left", width: "120px" },
  { label: "Emisión contrato", sortable: true, searchForColumn: true, key: "FechaEmisionContrato", selector: row => row?.FechaEmisionContrato?.replaceAll(".", "-"), headerAlign: "left", dataAlign: "left", width: "140px" },
  { label: "Id interno Webdox", sortable: false, searchForColumn: false, key: "id_wf", selector: row => (row.id_wf ? row.id_wf : "---"), headerAlign: "left", dataAlign: "left", width: "130px" },
  {
    label: "Estado colaborador",
    sortable: true,
    searchForColumn: true,
    key: "estado_colaborador",
    selector: renderEstadoColaborador,
    headerAlign: "left",
    dataAlign: "center",
    width: "140px",
  },
  { label: "% Avance", sortable: false, searchForColumn: false, key: "porcentaje_avance", selector: row => (row.porcentaje_avance != null ? row.porcentaje_avance : 0), headerAlign: "left", dataAlign: "left", width: "90px" },
  {
    label: "Recordatorio",
    sortable: true,
    searchForColumn: false,
    key: "reenvio_contrato",
    selector: row => renderBtnReenvioWebdox(row, reenviarContrato),
    headerAlign: "left",
    dataAlign: "center",
    width: "120px",
  },
];

const MonitorWebdox = () => {
  const {
    handleRowDoubleClick,
    showModal,
    setShowModal,
    selectedRow,
    fetchTableData,
    selectedRows,
    setSelectedRows,
    refreshToggle,
    cancelarEnWebdox,
    reenviarAConfirmalo,
    reenviarContrato,
  } = useMonitor(1);

  const initialSortBy = "IdLote";
  const initialSortDir = "desc";

  const columnsDef = useMemo(
    () => getMonitorWebdoxColumns(reenviarContrato),
    [reenviarContrato],
  );

  return (
    <>
      {/* Datatable dinámico con los datos del monitor */}
      <Datatable
        refreshToggle={refreshToggle}
        checkboxColumn={true}
        onSelectionChange={setSelectedRows}
        rowIdField="id"
        storageKey="monitor-webdox-table"
        titletable=""
        requestDataFn={fetchTableData}
        initialPerPage={10}
        columns={columnsDef}
        orderColumns={true}
        adjustColumnSize={true}
        searchEnabled={true}
        searchColumnsEnabled={true}
        manualPageInput={true}
        onRowDoubleClick={handleRowDoubleClick}
        rowsPerPageOptions={[10, 20, 50, 100]}
        initialSortBy={initialSortBy}
        initialSortDir={initialSortDir}
      />

      {/* Botones inferiores: Descargar Excel y Acciones Masivas */}
      <div className="row mt-3 align-items-center">
        <div className="col-md-6 col-sm-12 mb-2 mb-md-0">
          <BtnEnviarGenerarExcel ubicacionArchivo="excelMonitor/ReporteDataMonitorWebdox.xlsx" agregarFecha={true} />
        </div>
        <div className="col-md-6 col-sm-12 d-flex justify-content-md-end justify-content-start align-items-center gap-2 flex-wrap">
          {selectedRows && selectedRows.length > 0 && (
            <>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm rounded-pill px-3 py-2 fw-bold shadow-none"
                onClick={() => {
                  const ids = selectedRows.map(r => (typeof r === "object" && r !== null ? r.id : r)).filter(Boolean);
                  cancelarEnWebdox({ ids });
                }}
              >
                <i className="bi bi-x-circle me-1"></i> Cancelar en Webdox ({selectedRows.length})
              </button>
              <button
                type="button"
                className="btn btn-sm rounded-pill px-3 py-2 fw-bold text-white shadow-none"
                style={{ backgroundColor: "#2a77b9", borderColor: "#2a77b9" }}
                onClick={() => {
                  const ids = selectedRows.map(r => (typeof r === "object" && r !== null ? r.id : r)).filter(Boolean);
                  reenviarAConfirmalo({ ids });
                }}
              >
                <i className="bi bi-arrow-repeat me-1"></i> Reenviar a Confírmalo ({selectedRows.length})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal de detalles del registro */}
      <MonitorModal
        show={showModal}
        row={selectedRow}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};

export default MonitorWebdox;
